import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PurchaseOrderService } from '../purchase-order.service';

export interface PoApprovalQuery {
  searchParam: string;
  userId: number;
  docStatus: string;
  fromDate: string | null;
  toDate: string | null;
  sortField: string;
  sortDirection: 'asc' | 'desc' | string;
  limit: number;
  offset: number;
  activeFlag: 0 | 1; // 1 = active, 0 = archived
  operationType?: string; // defaults to 'buyerPurchaseOrderApproval'
}

interface CacheEntry<T> {
  subject: BehaviorSubject<T | null>;
  updatedAt: number;
  inFlight?: Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class BuyerApprovalPoSWRService {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly TTL_MS = 90_000; // 90 seconds

  constructor(private poService: PurchaseOrderService) {}

  getList$(q: PoApprovalQuery, revalidate = true): Observable<any | null> {
    const key = this.keyOf(q);
    let entry = this.cache.get(key);
    if (!entry) {
      entry = { subject: new BehaviorSubject<any | null>(null), updatedAt: 0 };
      this.cache.set(key, entry);
    }

    const isFresh = Date.now() - entry.updatedAt < this.TTL_MS;
    if ((!isFresh || entry.subject.getValue() === null) && revalidate) {
      this.refresh(q);
    }
    return entry.subject.asObservable();
  }

  getCached(q: PoApprovalQuery): any | null {
    const key = this.keyOf(q);
    const entry = this.cache.get(key);
    return entry ? entry.subject.getValue() : null;
  }

  async refresh(q: PoApprovalQuery): Promise<void> {
    const key = this.keyOf(q);
    const entry = this.cache.get(key) || {
      subject: new BehaviorSubject<any | null>(null),
      updatedAt: 0,
    };
    this.cache.set(key, entry);
    if (entry.inFlight) return entry.inFlight;

    entry.inFlight = (async () => {
      const resp = await this.poService.fetchAllPurchaseOrderApprovalByBuyerId(
        q.searchParam,
        q.userId,
        q.docStatus,
        q.fromDate,
        q.toDate,
        q.sortField,
        q.sortDirection,
        q.limit,
        q.offset,
        q.activeFlag,
        q.operationType ?? 'buyerPurchaseOrderApproval'
      );
      entry.updatedAt = Date.now();
      entry.subject.next(resp);
      entry.inFlight = undefined;
    })().catch(() => {
      entry.inFlight = undefined;
    });

    return entry.inFlight;
  }

  invalidateMatching(predicate: (key: string) => boolean) {
    [...this.cache.keys()].forEach((k) => {
      if (predicate(k)) this.cache.delete(k);
    });
  }

  invalidateAll() {
    this.cache.clear();
  }

  keyOf(q: PoApprovalQuery): string {
    return 'buyer-approval-po:' + JSON.stringify(q);
  }

  patchList(q: PoApprovalQuery, mutator: (resp: any) => any): void {
    const key = this.keyOf(q);
    const entry = this.cache.get(key);
    if (!entry) return;
    const current = entry.subject.getValue();
    if (!current) return;
    try {
      const next = mutator(current);
      entry.subject.next(next);
      entry.updatedAt = Date.now();
    } catch {
      // ignore mutator errors
    }
  }
}

